import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  ActuatorHealth,
  ActuatorHealthStatus,
  ActuatorInfo,
  getActuatorHealth,
  getActuatorInfo,
} from 'services/api/api-actuator';

export interface ActuatorService {
  key: string;
  label: string;
  path: string;
}

export const ACTUATOR_SERVICES: ActuatorService[] = [
  { key: 'gdp', label: 'GDP', path: '/gdp' },
  { key: 'gri', label: 'GRI', path: '/gri' },
  { key: 'sac', label: 'SAC-GW', path: '/sac' },
  { key: 'gcem', label: 'GCEM', path: '/gcem' },
  { key: 'tcp', label: 'Sograce TCP', path: '/tcp' },
];

type RequestStatus = 'idle' | 'loading' | 'failed';

export interface ServiceStatus {
  info: ActuatorInfo | null;
  health: ActuatorHealth | null;
  infoStatus: RequestStatus;
  healthStatus: RequestStatus;
}

export interface SystemStatusState {
  services: Record<string, ServiceStatus>;
}

const emptyService = (): ServiceStatus => ({
  info: null,
  health: null,
  infoStatus: 'idle',
  healthStatus: 'idle',
});

const initialState: SystemStatusState = {
  services: ACTUATOR_SERVICES.reduce<Record<string, ServiceStatus>>((acc, service) => {
    acc[service.key] = emptyService();
    return acc;
  }, {}),
};

export const fetchActuatorInfoAsync = createAsyncThunk(
  'systemStatus/fetchActuatorInfo',
  async (service: ActuatorService) => {
    const response = await getActuatorInfo(service.path);
    return { key: service.key, info: response.data };
  },
);

export const fetchActuatorHealthAsync = createAsyncThunk(
  'systemStatus/fetchActuatorHealth',
  async (service: ActuatorService) => {
    const response = await getActuatorHealth(service.path);
    return { key: service.key, health: response.data };
  },
);

export const fetchAllSystemStatusAsync = createAsyncThunk(
  'systemStatus/fetchAll',
  async (_, { dispatch }) => {
    await Promise.all(
      ACTUATOR_SERVICES.flatMap((service) => [
        dispatch(fetchActuatorInfoAsync(service)),
        dispatch(fetchActuatorHealthAsync(service)),
      ]),
    );
  },
);

const systemStatusSlice = createSlice({
  name: 'systemStatus',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchActuatorInfoAsync.pending, (state, action) => {
        const key = action.meta.arg.key;
        state.services[key].infoStatus = 'loading';
      })
      .addCase(fetchActuatorInfoAsync.fulfilled, (state, action) => {
        const { key, info } = action.payload;
        state.services[key].info = info;
        state.services[key].infoStatus = 'idle';
      })
      .addCase(fetchActuatorInfoAsync.rejected, (state, action) => {
        const key = action.meta.arg.key;
        state.services[key].infoStatus = 'failed';
      })
      .addCase(fetchActuatorHealthAsync.pending, (state, action) => {
        const key = action.meta.arg.key;
        state.services[key].healthStatus = 'loading';
      })
      .addCase(fetchActuatorHealthAsync.fulfilled, (state, action) => {
        const { key, health } = action.payload;
        state.services[key].health = health;
        state.services[key].healthStatus = 'idle';
      })
      .addCase(fetchActuatorHealthAsync.rejected, (state, action) => {
        const key = action.meta.arg.key;
        state.services[key].healthStatus = 'failed';
      });
  },
});

export const selectServiceStatus = (key: string) => (state: { systemStatus: SystemStatusState }) =>
  state.systemStatus.services[key];

export const getCommitFromInfo = (info: ActuatorInfo | null): string | null => {
  if (!info?.git?.commit) return null;
  const commit = info.git.commit as { id?: string; 'id.abbrev'?: string };
  if (typeof commit.id === 'string' && commit.id.length >= 8) return commit.id.slice(0, 8);
  return commit['id.abbrev'] ?? null;
};

export const getVersionFromInfo = (info: ActuatorInfo | null): string | null =>
  info?.build?.version ?? null;

export const getHealthStatus = (health: ActuatorHealth | null): ActuatorHealthStatus | null =>
  health?.status ?? null;

export default systemStatusSlice.reducer;
